import { SpaceAnalyticsCreateLocation } from '@fullstory/analytics';
import { EveryoneSegmentId } from '@fullstory/client-api';
import {
  Box,
  Icon,
  InlineLink,
  List,
  ListDivider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
} from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { ObjectType, Space } from '@fullstory/proto/fs/services/dx-objects';
import { useSelectableList } from '@fullstory/react-hooks';
import {
  DASHBOARD_CREATE_ROUTE,
  DATA_TABLE_CREATE_ROUTE,
  FUNNEL_CREATE_ROUTE,
  generateRoutePath,
  HEATMAP_CREATE_ROUTE,
  JOURNEY_CREATE_ROUTE,
  LibraryObjectType,
  METRIC_CREATE_ROUTE,
  RETENTION_CREATE_ROUTE,
  SEGMENT_ROUTE,
} from '@fullstory/routes';
import { useShowTemplateGalleryModal } from '@fullstory/template-gallery-model';
import { createPath } from 'history';
import React, { useMemo } from 'react';
import { NEW_SEGMENT_QUERY_PARAM, PSEUDO_SPACES } from '../../constants';
import { useActiveSpaceContext } from '../../contexts';
import { LibraryAccessMap } from '../../hooks';
import { objectTypeAnalyticsString } from '../../utils';
import { ViewOnlyTooltip } from '../ViewOnlyTooltip';

interface Option {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentProps<typeof Icon>['name'];
  objectType: LibraryObjectType;
  // If set, will generate a path and render this option as a link.
  // If not set, can use the onClick callback on the <ListItem>
  path?: (orgId: string) => string;
}

const templateGalleryOption: Option = {
  id: 'template-gallery-option',
  title: intl.formatMessage({ defaultMessage: 'Create with templates', id: 'opeiUM' }),
  description: '',
  icon: 'dashboard',
  objectType: ObjectType.DASHBOARD, // TODO(sarahgrace): Figure out what icon we want here.
};

const segmentOption: Option = {
  id: 'segment-option',
  title: intl.formatMessage({ defaultMessage: 'Segment', id: 'OrwKQ+' }),
  description: intl.formatMessage({
    defaultMessage: 'Group of users that can be analyzed across Fullstory.',
    id: 'eU5wlo',
  }),
  icon: 'segments',
  objectType: ObjectType.SEGMENT,
  path: orgId =>
    createPath({
      pathname: generateRoutePath(SEGMENT_ROUTE, {
        orgId,
        segmentId: EveryoneSegmentId,
      }),
      search: `?${NEW_SEGMENT_QUERY_PARAM}=true`,
    }),
};

const metricOption: Option = {
  id: 'metric-option',
  title: intl.formatMessage({ defaultMessage: 'Metric', id: 'yypztr' }),
  description: intl.formatMessage({
    defaultMessage: 'Capture key interactions and track KPI’s.',
    id: 'fIYQ8f',
  }),
  icon: 'metrics',
  objectType: ObjectType.METRIC,
  path: orgId =>
    createPath({
      pathname: generateRoutePath(METRIC_CREATE_ROUTE, {
        orgId,
      }),
    }),
};
const funnelOption: Option = {
  id: 'funnel-option',
  title: intl.formatMessage({ defaultMessage: 'Funnel', id: 'vOE+PU' }),
  description: intl.formatMessage({
    defaultMessage: 'Monitor and analyze actions towards a goal.',
    id: '2T0BrA',
  }),
  icon: 'funnels',
  objectType: ObjectType.FUNNEL,
  path: orgId =>
    createPath({
      pathname: generateRoutePath(FUNNEL_CREATE_ROUTE, {
        orgId,
      }),
    }),
};
const retentionOption: Option = {
  id: 'retention-option',
  title: intl.formatMessage({ defaultMessage: 'Retention', id: 'nL6Jl0' }),
  description: intl.formatMessage({
    defaultMessage: 'Understand the frequency and actions of returning users.',
    id: 'rtMDR9',
  }),
  icon: 'retention',
  objectType: ObjectType.RETENTION,
  path: orgId =>
    createPath({
      pathname: generateRoutePath(RETENTION_CREATE_ROUTE, {
        orgId,
      }),
    }),
};
const journeyOption: Option = {
  id: 'journey-option',
  title: intl.formatMessage({ defaultMessage: 'Journey', id: 'btihtD' }),
  description: intl.formatMessage({
    defaultMessage: 'Shows the most common paths from an entry or exit point.',
    id: '8n4C8a',
  }),
  icon: 'journeys',
  objectType: ObjectType.JOURNEY,
  path: orgId =>
    createPath({
      pathname: generateRoutePath(JOURNEY_CREATE_ROUTE, {
        orgId,
      }),
    }),
};
const heatmapOption: Option = {
  id: 'heatmap-option',
  title: intl.formatMessage({ defaultMessage: 'Heatmap', id: 'WEDRDw' }),
  description: intl.formatMessage({
    defaultMessage: 'Visual, aggregated data about how users engage with your pages.',
    id: 'd3B5/E',
  }),
  icon: 'heatmap',
  objectType: ObjectType.HEATMAP,
  path: orgId =>
    createPath({
      pathname: generateRoutePath(HEATMAP_CREATE_ROUTE, {
        orgId,
      }),
    }),
};
const dashboardOption: Option = {
  id: 'dashboard-option',
  title: intl.formatMessage({ defaultMessage: 'Dashboard', id: 'hzSNj4' }),
  description: intl.formatMessage({
    defaultMessage: 'Custom visualization using your Fullstory data.',
    id: '9aClM6',
  }),
  icon: 'dashboard',
  objectType: ObjectType.DASHBOARD,
  path: orgId =>
    generateRoutePath(DASHBOARD_CREATE_ROUTE, {
      orgId,
    }),
};
const dataTableOption: Option = {
  id: 'data-table-option',
  title: intl.formatMessage({ defaultMessage: 'Table', id: 'IeBejt' }),
  description: intl.formatMessage({
    defaultMessage: 'Compare metrics and funnel conversion rates',
    id: 'TS4Rhy',
  }),
  icon: 'data-table',
  objectType: ObjectType.DATA_TABLE,
  path: orgId =>
    generateRoutePath(DATA_TABLE_CREATE_ROUTE, {
      orgId,
    }),
};
const optionGroups: Option[][] = [
  [templateGalleryOption],
  [segmentOption, metricOption, funnelOption, retentionOption, journeyOption, heatmapOption],
  [dashboardOption, dataTableOption],
];

const allOptions = optionGroups.flat();

const dataFsPropertiesSchema =
  '{"data-library-object":"str","data-object-type":"str","data-location":"str"}';
const LIST_ITEM_TEST_ID = 'create-menu-select-item';

interface CreateMenuSelectProps {
  anchor: React.ComponentProps<typeof Popover>['anchor'];
  onCloseSignal: React.ComponentProps<typeof Popover>['onCloseSignal'];
  placement?: React.ComponentProps<typeof Popover>['placement'];
  offset?: React.ComponentProps<typeof Popover>['offset'];
  testId?: React.ComponentProps<typeof Popover>['testId'];
  variant?: React.ComponentProps<typeof Popover>['variant'];
  open: boolean;
  orgId: string;
  activeSpace?: Space;
  fallbackSpace?: Space;
  enabledObjectTypes: LibraryAccessMap;
  requireCardSupport?: boolean;
  location?: SpaceAnalyticsCreateLocation;
  /**
   * When adding from a Featured section,
   * will be used to initially select the Space Section to feature on during Save.
   */
  sectionId?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export const CreateMenuSelect: React.FC<CreateMenuSelectProps> = ({
  orgId,
  anchor,
  onCloseSignal,
  placement = 'bottom-start',
  offset = 8,
  open,
  activeSpace,
  fallbackSpace,
  enabledObjectTypes,
  requireCardSupport,
  location,
  sectionId,
  onMouseEnter,
  onMouseLeave,
  testId,
  variant = 'bordered',
}) => {
  const { setActiveSpace } = useActiveSpaceContext();
  const enabledOptionGroups = useMemo(
    () =>
      optionGroups.map(group =>
        group.filter(
          o =>
            enabledObjectTypes[o.objectType].included &&
            enabledObjectTypes[o.objectType].supportsLibrary &&
            (requireCardSupport ? enabledObjectTypes[o.objectType].supportsCards : true),
        ),
      ),
    [enabledObjectTypes, requireCardSupport],
  );
  const { setShowing } = useShowTemplateGalleryModal();

  // Ignore home/private, if no space use provided fallback
  const spaceToUse = useMemo(
    () =>
      PSEUDO_SPACES.includes(activeSpace?.id ?? '') && !activeSpace ? fallbackSpace : activeSpace,
    [activeSpace, fallbackSpace],
  );

  const customHandlers: Record<string, () => void> = {
    [templateGalleryOption.id]: () => {
      setShowing(true);
      onCloseSignal?.();
    },
  };

  const handleListItemClick = (id: string) => {
    // Invoke custom handler logic (if one exists)
    customHandlers[id]?.();

    // Perform shared logic
    if (spaceToUse) {
      setActiveSpace({
        space: spaceToUse,
        sectionId,
      });
    }
  };

  return (
    <Popover
      data-component="CreateMenuSelectBase"
      open={open}
      anchor={anchor}
      onCloseSignal={onCloseSignal}
      offset={offset}
      placement={placement}
      width={468}
      variant={variant}
      borderRadius={8}
      overflow="auto"
      testId={testId}
    >
      {/* more ideally this is on Popover since the Popover has borders */}
      <div onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        <List>
          {enabledOptionGroups.map((group, i) => (
            <React.Fragment key={`group-${group[i]?.id}`}>
              {i > 0 && group.length > 0 && <ListDivider />}
              {group.map(opt => (
                <ListItem
                  orgId={orgId}
                  option={opt}
                  key={opt.id}
                  onCloseSignal={onCloseSignal}
                  onClick={() => handleListItemClick(opt.id)}
                  testId={LIST_ITEM_TEST_ID}
                  disabled={enabledObjectTypes[opt.objectType].disabled ?? false}
                  data-object-type={objectTypeAnalyticsString[opt.objectType]}
                  data-location={location ?? ''}
                  data-fs-properties-schema={dataFsPropertiesSchema}
                />
              ))}
            </React.Fragment>
          ))}
        </List>
      </div>
    </Popover>
  );
};

interface ListItemProps extends Omit<React.ComponentProps<typeof MenuItem>, 'children'> {
  orgId: string;
  option: Option;
  onCloseSignal: React.ComponentProps<typeof Popover>['onCloseSignal'];
  onClick: () => void;
}

const ListItem: React.FC<ListItemProps> = ({
  orgId,
  option,
  onCloseSignal,
  onClick,
  disabled,
  ...rest
}) => {
  const { getListItemProps } = useSelectableList(
    allOptions,
    () => true,
    () => onCloseSignal?.(),
    { active: true },
  );

  const path =
    option.path &&
    createPath({
      pathname: option.path(orgId),
    });

  const children = (
    <>
      <Box paddingLeft={12}>
        <ListItemIcon size={20} name={option.icon} color="default" />
      </Box>
      <Box padding={[8, 12, 8, 0]} width="100%">
        <ListItemText text={option.title} subtext={option.description} />
      </Box>
    </>
  );

  return (
    <ViewOnlyTooltip placement="right" isViewOnly={disabled}>
      <MenuItem
        disablePadding
        disableGutters
        data-component="ListItem"
        key={option.title}
        disabled={disabled}
        {...getListItemProps(option)}
        {...rest}
      >
        {disabled ? (
          <Box style={{ textDecoration: 'none', display: 'contents', padding: '8px 12px' }}>
            {children}
          </Box>
        ) : path ? (
          <InlineLink
            to={path}
            style={{ textDecoration: 'none', display: 'contents', padding: '8px 12px' }}
            onClick={onClick}
          >
            {children}
          </InlineLink>
        ) : (
          <InlineLink
            style={{ textDecoration: 'none', display: 'contents', padding: '8px 12px' }}
            onClick={onClick}
          >
            {children}
          </InlineLink>
        )}
      </MenuItem>
    </ViewOnlyTooltip>
  );
};
